import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/ui/Button';
import Panel from '../components/ui/Panel';

interface FeatureCardProps {
  title: string;
  description: string;
}

const FeatureCard: React.FC<FeatureCardProps> = memo(({ title, description }) => (
  <div className="text-center p-4">
    <div className="text-osrs-gold text-lg font-bold mb-2" role="heading" aria-level={3}>
      {title}
    </div>
    <p className="text-osrs-yellow">
      {description}
    </p>
  </div>
));

FeatureCard.displayName = 'FeatureCard';

const HomePage: React.FC = memo(() => {
  return (
    <div className="space-y-10">
      <section className="text-center py-12" role="banner">
        <h1 className="text-4xl lg:text-5xl text-osrs-gold font-bold uppercase mb-6">
          OSRS Design System
        </h1>
        <p className="text-osrs-yellow text-xl max-w-3xl mx-auto mb-8">
          A comprehensive design system based on Old School RuneScape's iconic interface.
          Build consistent, authentic OSRS-styled experiences.
        </p>
        <div className="flex gap-4 justify-center flex-wrap">
          <Link to="/foundations/colors" aria-label="Get started with the design system">
            <Button size="large">Get Started</Button>
          </Link>
          <Link to="/components" aria-label="Browse all components">
            <Button size="large" variant="secondary">Browse Components</Button>
          </Link>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-6" role="region" aria-label="Main sections">
        <Panel title="Foundations">
          <p className="text-osrs-yellow mb-4">
            Explore the core building blocks of the OSRS interface, including colors, typography, spacing, and more.
          </p>
          <Link to="/foundations/colors" aria-label="Explore foundation elements">
            <Button variant="secondary" fullWidth>Explore Foundations</Button>
          </Link>
        </Panel>
        
        <Panel title="Components">
          <p className="text-osrs-yellow mb-4">
            Discover a wide range of UI components from the OSRS interface, including buttons, panels, inventories, and more.
          </p>
          <Link to="/components" aria-label="Explore UI components">
            <Button variant="secondary" fullWidth>Explore Components</Button>
          </Link>
        </Panel>
        
        <Panel title="Patterns">
          <p className="text-osrs-yellow mb-4">
            Learn how to combine components into common UI patterns for navigation, forms, dialogs, and more.
          </p>
          <Link to="/patterns" aria-label="Explore UI patterns">
            <Button variant="secondary" fullWidth>Explore Patterns</Button>
          </Link>
        </Panel>
      </section>
      
      <section className="border-3 border-osrs-gold bg-osrs-dark-brown p-6 rounded-lg" role="region" aria-label="Features">
        <div className="text-center mb-6">
          <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-3">Why Use This Design System?</h2>
          <p className="text-osrs-yellow max-w-3xl mx-auto">
            Creating authentic OSRS-styled interfaces requires attention to detail. This design system provides you with all the tools needed.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FeatureCard
            title="Authentic"
            description="Recreates the iconic OSRS look and feel with pixel-perfect accuracy."
          />
          <FeatureCard
            title="Growing library"
            description="Import Button, Panel, Input, Checkbox, Dialog, Tooltip, Scrollbar, ProgressBar, Inventory, ChatBox, and the rest from one package."
          />
          <FeatureCard
            title="Documented"
            description="Every foundations, component, pattern, and resource page shows a working example you can copy."
          />
          <FeatureCard
            title="Flexible"
            description="Easily adaptable for fan sites, tools, and community projects."
          />
        </div>
      </section>
      
      <section className="text-center py-6" role="region" aria-label="Call to action">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-4">Ready to Get Started?</h2>
        <p className="text-osrs-yellow mb-6">
          Explore the foundation elements or jump straight to components.
        </p>
        <div className="flex gap-4 justify-center flex-wrap">
          <Link to="/foundations/colors" aria-label="Start exploring foundations">
            <Button>Explore Foundations</Button>
          </Link>
          <Link to="/components/button" aria-label="View component library">
            <Button variant="secondary">View Components</Button>
          </Link>
        </div>
      </section>
    </div>
  );
});

HomePage.displayName = 'HomePage';

export default HomePage; 